/**
 * Единицы измерения маршрутов: время и расстояние.
 *
 * До этой утилиты время выводилось в минутах независимо от величины
 * (`~1957 мин` вместо 32 часов), а `(duration / 60).toFixed(1)` давал
 * «3.3 ч» вместо «3 ч 20 мин». Правила — BRANDBOOK.md, раздел 6.2.
 */

import { plural } from './plural';

const HOURS = ['час', 'часа', 'часов'];
const DAYS = ['день', 'дня', 'дней'];

/**
 * Время прохождения маршрута.
 *
 * | Диапазон | Формат      |
 * |----------|-------------|
 * | < 60 мин | `45 мин`    |
 * | 1–24 ч   | `3 ч 20 мин`|
 * | > 24 ч   | `~2 дня`    |
 *
 * @param {number} minutes - Длительность в минутах
 * @returns {string|null} - Готовая строка либо null, если значения нет
 */
export const formatDuration = (minutes) => {
  const value = Number(minutes);
  if (!Number.isFinite(value) || value <= 0) return null;

  const total = Math.round(value);

  if (total < 60) return `${total} мин`;

  // Сутки и больше: точность до минут здесь ничего не решает, а строка
  // «26 ч 40 мин» читается хуже, чем «~1 день»
  if (total >= 24 * 60) {
    const days = Math.round(total / (24 * 60));
    return `~${days} ${plural(days, DAYS)}`;
  }

  const hours = Math.floor(total / 60);
  const rest = total % 60;
  return rest === 0 ? `${hours} ${plural(hours, HOURS)}` : `${hours} ч ${rest} мин`;
};

/**
 * Расстояние маршрута в километрах: десятичная запятая, а не точка.
 *
 * Отличается от `formatApproxDistance`: здесь величина внесена
 * редакцией в админку, это не прикидка по прямой — знак «~» не нужен.
 *
 * @param {number} km - Расстояние в километрах
 * @returns {string|null}
 */
export const formatRouteDistance = (km) => {
  const value = Number(km);
  if (!Number.isFinite(value) || value <= 0) return null;

  if (value < 1) return `${Math.round(value * 1000)} м`;

  const rounded = value < 10 ? Math.round(value * 10) / 10 : Math.round(value);
  return `${String(rounded).replace('.', ',')} км`;
};

/**
 * Расстояние, посчитанное по прямой (гаверсинус), а не по пешему пути.
 *
 * Шаг округления растёт вместе с расстоянием, знак «~» обязателен:
 * он сообщает, что это прикидка. Брендбук 6.2.
 *
 * @param {number} km - Расстояние в километрах
 * @returns {string|null}
 */
export const formatApproxDistance = (km) => {
  const value = Number(km);
  if (!Number.isFinite(value) || value <= 0) return null;

  const meters = value * 1000;

  // Округление считается до сравнения с границей: 98 м с шагом 10 даёт
  // ровно 100 — это уже «~100 м», а не «~1000 м» из следующей ступени
  if (meters < 100) return `~${Math.max(10, Math.round(meters / 10) * 10)} м`;
  if (meters < 1000) {
    const rounded = Math.round(meters / 50) * 50;
    return rounded >= 1000 ? '~1 км' : `~${rounded} м`;
  }
  if (value < 10) return `~${String(Math.round(value * 10) / 10).replace('.', ',')} км`;
  return `~${Math.round(value)} км`;
};
